import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Pagination,
  Popover,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { surveyService } from '../../services/survey.service.ts';
import { responseService } from '../../services/response.service.ts';
import { engagementService } from '../../services/engagement.service';
import { useOpenGraph } from '../../hooks/useOpenGraph';
import { ShareButtons } from '../../components/common/ShareButtons';
import { Survey, SurveyStatus } from '../../types/survey.types';
import ResponseAuditModal from '../../components/survey/ResponseAuditModal';
import { parseApiError } from '../../utils/error-handler';

const { Text, Paragraph, Title } = Typography;

const BRAND_INDIGO = '#2E2B81';
const BRAND_GREEN = '#00984A';
const ITEMS_PER_PAGE = 6;

const SCOPE_LABELS: Record<string, string> = {
  NATIONAL: 'Nacional',
  STATE: 'Estadual',
  CITY: 'Municipal',
};

const SurveySharePopover = ({ shareUrl, surveyName, surveyHash }: { shareUrl: string; surveyName: string; surveyHash?: string }) => {
  // shareUrl já carrega o ?ref= do indicador (ver buildSurveyShareUrl) — o link
  // encurtado precisa do mesmo código, senão a indicação se perde na criação do ShortLink.
  const referralCode = new URL(shareUrl, window.location.origin).searchParams.get('ref') ?? undefined;

  return (
    <ShareButtons
      title={`Pesquisa: ${surveyName} - PePoll`}
      description="Participe desta pesquisa na PePoll"
      url={shareUrl}
      surveyHash={surveyHash}
      referralCode={referralCode}
      size="small"
      channels={['whatsapp', 'facebook', 'copy']}
      spacing="small"
    />
  );
};

// ── Card de pesquisa ativa ────────────────────────────────────────────────────

const ActiveSurveyCard = ({
  survey,
  onOpenAudit,
  shareUrl,
  surveyHash,
  auditLoading = false,
}: {
  survey: Survey;
  onOpenAudit: (survey: Survey) => void;
  shareUrl?: string;
  surveyHash?: string;
  auditLoading?: boolean;
}) => {
  const navigate = useNavigate();
  const isPeriodExpired = dayjs().isAfter(dayjs(survey.endDate));

  return (
    <Card
      style={{ width: '100%', height: '100%', borderLeft: `4px solid ${BRAND_GREEN}`, borderRadius: 8 }}
      styles={{ body: { padding: '20px 20px 16px' } }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <Title level={5} style={{ marginBottom: 0, flex: 1, color: BRAND_INDIGO, lineHeight: 1.4 }}>
            {survey.name}
          </Title>
          <Space size={6} style={{ flexShrink: 0 }}>
            {survey.hasUserResponded ? (
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginInlineEnd: 0 }}>
                Respondida
              </Tag>
            ) : (
              <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                Ativa
              </Tag>
            )}
            {shareUrl && (
              <Popover
                trigger="click"
                placement="bottomRight"
                content={<SurveySharePopover shareUrl={shareUrl} surveyName={survey.name} surveyHash={surveyHash} />}
              >
                <Button size="small" type="text" icon={<ShareAltOutlined />} aria-label="Compartilhar pesquisa" />
              </Popover>
            )}
          </Space>
        </div>

        <Text type={isPeriodExpired ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
          <CalendarOutlined style={{ marginRight: 5 }} />
          {dayjs(survey.startDate).format('DD/MM/YYYY')} {'\u2192'} {dayjs(survey.endDate).format('DD/MM/YYYY')}
          {isPeriodExpired && ' \u00b7 Per\u00edodo encerrado'}
        </Text>

        <Text type="secondary" style={{ fontSize: 12 }}>
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {SCOPE_LABELS[survey.geographicScope] ?? survey.geographicScope}
        </Text>

        {survey.description && (
          <Paragraph
            ellipsis={{ rows: 2, tooltip: survey.description }}
            style={{ marginBottom: 0, fontSize: 13, color: 'rgba(0,0,0,0.60)' }}
          >
            {survey.description}
          </Paragraph>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
          {survey.hasUserResponded ? (
            <Button
              size="small"
              icon={<FileSearchOutlined />}
              loading={auditLoading}
              onClick={() => onOpenAudit(survey)}
            >
              Ver Auditoria
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<FileTextOutlined />}
              disabled={isPeriodExpired}
              onClick={!isPeriodExpired ? () => navigate(`/surveys/${survey.id}/take`) : undefined}
            >
              Responder
            </Button>
          )}
        </div>
      </Space>
    </Card>
  );
};

// ── Card de pesquisa finalizada ───────────────────────────────────────────────

const CompletedSurveyCard = ({ survey }: { survey: Survey }) => {
  const navigate = useNavigate();

  return (
    <Card
      style={{ width: '100%', height: '100%', borderLeft: '4px solid #bfbfbf', borderRadius: 8 }}
      styles={{ body: { padding: '20px 20px 16px' } }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <Title level={5} style={{ marginBottom: 0, flex: 1, color: BRAND_INDIGO, lineHeight: 1.4 }}>
            {survey.name}
          </Title>
          <Tag color="default" style={{ flexShrink: 0, marginInlineEnd: 0 }}>
            Encerrada
          </Tag>
        </div>

        <Text type="secondary" style={{ fontSize: 12 }}>
          <CalendarOutlined style={{ marginRight: 5 }} />
          {dayjs(survey.startDate).format('DD/MM/YYYY')} {'\u2192'} {dayjs(survey.endDate).format('DD/MM/YYYY')}
        </Text>

        <Text type="secondary" style={{ fontSize: 12 }}>
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {SCOPE_LABELS[survey.geographicScope] ?? survey.geographicScope}
        </Text>

        {survey.description && (
          <Paragraph
            ellipsis={{ rows: 2, tooltip: survey.description }}
            style={{ marginBottom: 0, fontSize: 13, color: 'rgba(0,0,0,0.60)' }}
          >
            {survey.description}
          </Paragraph>
        )}

        {survey.participantCount !== undefined && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {survey.participantCount} {survey.participantCount === 1 ? 'resposta' : 'respostas'}
          </Text>
        )}

        <div style={{ paddingTop: 4 }}>
          <Button
            size="small"
            type="primary"
            icon={<BarChartOutlined />}
            onClick={() => navigate(`/surveys/${survey.id}/analytics`)}
          >
            Ver Indicadores
          </Button>
        </div>
      </Space>
    </Card>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────

const ParticipantDashboard = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auditLoadingSurveyId, setAuditLoadingSurveyId] = useState<string | null>(null);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<{
    responseHash: string;
    surveyName: string;
    surveyPublicAuditHash?: string;
  } | null>(null);
  const [activeSearch, setActiveSearch] = useState('');
  const [completedSearch, setCompletedSearch] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Configure Open Graph tags para esta página
  useOpenGraph({
    title: 'Centro de Pesquisas - PePoll',
    description: 'Participe das pesquisas ativas e acompanhe os resultados das pesquisas finalizadas na PePoll.',
    url: 'https://app.pepoll.org/surveys',
  });

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError('');

      const [activeRes, completedRes] = await Promise.all([
        surveyService.listSurveys({ status: SurveyStatus.ACTIVE, limit: 100 }),
        surveyService.listSurveys({ status: SurveyStatus.COMPLETED, limit: 100 }),
      ]);

      const surveysWithStatus = await Promise.all(
        activeRes.data.map(async (survey) => ({
          ...survey,
          hasUserResponded: await surveyService.hasUserResponded(survey.id),
        })),
      );

      setSurveys([...surveysWithStatus, ...completedRes.data]);

      try {
        const engagement = await engagementService.getCurrentCycle({ page: 1, pageSize: 10 });
        setReferralCode(engagement.referralLink?.code ?? null);
      } catch {
        setReferralCode(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar pesquisas');
    } finally {
      setLoading(false);
    }
  };

  const buildSurveyShareUrl = (surveyId: string, surveyHash?: string) => {
    if (surveyHash) {
      const base = new URL(`/public/surveys/${surveyHash}`, window.location.origin);
      if (referralCode) {
        base.searchParams.set('ref', referralCode);
      }
      return base.toString();
    }
    const base = new URL('/signup', window.location.origin);
    base.searchParams.set('surveyId', surveyId);
    if (referralCode) {
      base.searchParams.set('ref', referralCode);
    }
    return base.toString();
  };

  const activeSurveys = surveys.filter((s) => s.status === SurveyStatus.ACTIVE);
  const completedSurveys = surveys.filter((s) => s.status === SurveyStatus.COMPLETED);

  const filteredActive = useMemo(
    () =>
      activeSearch.trim()
        ? activeSurveys.filter((s) => s.name.toLowerCase().includes(activeSearch.toLowerCase()))
        : activeSurveys,
    [activeSurveys, activeSearch],
  );

  const filteredCompleted = useMemo(
    () =>
      completedSearch.trim()
        ? completedSurveys.filter((s) => s.name.toLowerCase().includes(completedSearch.toLowerCase()))
        : completedSurveys,
    [completedSurveys, completedSearch],
  );

  const paginatedActive = filteredActive.slice(
    (activePage - 1) * ITEMS_PER_PAGE,
    activePage * ITEMS_PER_PAGE,
  );
  const paginatedCompleted = filteredCompleted.slice(
    (completedPage - 1) * ITEMS_PER_PAGE,
    completedPage * ITEMS_PER_PAGE,
  );

  const handleOpenAudit = async (survey: Survey) => {
    try {
      setAuditLoadingSurveyId(survey.id);
      const auditData = await responseService.getResponseAudit(survey.id);

      if (!auditData?.responseHash) {
        message.error('Não foi possível localizar a auditoria desta resposta.');
        return;
      }

      setSelectedAudit({
        responseHash: auditData.responseHash,
        surveyName: survey.name,
        surveyPublicAuditHash: survey.publicAuditHash,
      });
      setAuditModalOpen(true);
    } catch (err) {
      const apiError = parseApiError(err);
      message.error(apiError.message);
    } finally {
      setAuditLoadingSurveyId(null);
    }
  };

  const handleCloseAudit = () => {
    setAuditModalOpen(false);
    setSelectedAudit(null);
  };

  const ActiveSurveyCardWithAudit = ({ survey }: { survey: Survey }) => (
    <ActiveSurveyCard
      survey={survey}
      onOpenAudit={handleOpenAudit}
      shareUrl={buildSurveyShareUrl(survey.id, survey.publicAuditHash)}
      surveyHash={survey.publicAuditHash}
      auditLoading={auditLoadingSurveyId === survey.id}
    />
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 384 }}>
        <Spin size="large" tip="Carregando pesquisas..." />
      </div>
    );
  }

  const SectionHeader = ({ label, color, count }: { label: string; color: string; count: number }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <div style={{ width: 4, height: 22, background: color, borderRadius: 2, flexShrink: 0 }} />
      <Title level={4} style={{ marginBottom: 0, color: BRAND_INDIGO }}>{label}</Title>
      <Badge count={count} style={{ backgroundColor: color }} />
    </div>
  );

  const SurveyGrid = ({
    items,
    CardComp,
    page,
    total,
    onPage,
  }: {
    items: Survey[];
    CardComp: React.ComponentType<{ survey: Survey }>;
    page: number;
    total: number;
    onPage: (p: number) => void;
  }) => (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        {items.map((survey) => (
          <Col xs={24} md={12} key={survey.id}>
            <CardComp survey={survey} />
          </Col>
        ))}
      </Row>
      {Math.ceil(total / ITEMS_PER_PAGE) > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={page}
            total={total}
            pageSize={ITEMS_PER_PAGE}
            onChange={onPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </Space>
  );

  return (
    <div style={{ maxWidth: 1152, margin: '0 auto', padding: '32px 24px' }}>
      <Title level={2} style={{ color: BRAND_INDIGO, marginBottom: 4 }}>
        Centro de Pesquisas
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Participe das pesquisas ativas e acompanhe os resultados das pesquisas finalizadas
      </Text>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      {/* Pesquisas Ativas */}
      <section style={{ marginBottom: 48 }}>
        <SectionHeader label="Pesquisas Ativas" color={BRAND_GREEN} count={filteredActive.length} />
        <Input.Search
          placeholder="Buscar pesquisa por nome..."
          allowClear
          value={activeSearch}
          onChange={(e) => { setActiveSearch(e.target.value); setActivePage(1); }}
          style={{ marginBottom: 20 }}
          size="large"
        />
        {filteredActive.length === 0 ? (
          <Empty description="Nenhuma pesquisa ativa encontrada" style={{ padding: '40px 0' }} />
        ) : (
          <SurveyGrid
            items={paginatedActive}
            CardComp={ActiveSurveyCardWithAudit}
            page={activePage}
            total={filteredActive.length}
            onPage={setActivePage}
          />
        )}
      </section>

      {/* Pesquisas Finalizadas */}
      <section>
        <SectionHeader label="Pesquisas Finalizadas" color="#8c8c8c" count={filteredCompleted.length} />
        <Input.Search
          placeholder="Buscar pesquisa por nome..."
          allowClear
          value={completedSearch}
          onChange={(e) => { setCompletedSearch(e.target.value); setCompletedPage(1); }}
          style={{ marginBottom: 20 }}
          size="large"
        />
        {filteredCompleted.length === 0 ? (
          <Empty description="Nenhuma pesquisa finalizada encontrada" style={{ padding: '40px 0' }} />
        ) : (
          <SurveyGrid
            items={paginatedCompleted}
            CardComp={CompletedSurveyCard}
            page={completedPage}
            total={filteredCompleted.length}
            onPage={setCompletedPage}
          />
        )}
      </section>

      {selectedAudit && (
        <ResponseAuditModal
          open={auditModalOpen}
          onClose={handleCloseAudit}
          responseHash={selectedAudit.responseHash}
          surveyName={selectedAudit.surveyName}
          surveyPublicAuditHash={selectedAudit.surveyPublicAuditHash}
        />
      )}
    </div>
  );
};

export default ParticipantDashboard;
